import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Phone,
  Target,
  Calendar,
  RefreshCw,
  Download,
  Brain,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  UserCheck,
  PhoneCall,
  XCircle
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#0e7490', '#10b981', '#f97316', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const dispositionColors = {
  not_contacted: "#94a3b8",
  call_not_picked: "#f97316",
  call_later: "#3b82f6",
  not_interested: "#ef4444",
  wrong_number: "#dc2626",
  interested: "#22c55e",
  appointment_booked: "#8b5cf6",
  converted: "#10b981",
  dnd: "#b91c1c",
  language_barrier: "#eab308",
  already_customer: "#06b6d4",
  callback_requested: "#6366f1",
  busy: "#fb923c",
  voicemail: "#64748b",
  doctor_patient: "#f43f5e",
  budget_issue: "#f59e0b",
  camp_registration: "#14b8a6",
};

export default function CRMReportsPage({ user, logout }) {
  const [activeTab, setActiveTab] = useState("disposition");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("all");
  const [startDate, setStartDate] = useState("2020-01-01");
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  
  // Data states
  const [dispositionSummary, setDispositionSummary] = useState([]);
  const [sourceSummary, setSourceSummary] = useState([]);
  const [callerPerformance, setCallerPerformance] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [selectedCaller, setSelectedCaller] = useState("all");

  useEffect(() => {
    fetchAllData();
  }, [startDate, endDate, selectedCaller]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    switch(range) {
      case "today":
        setStartDate(format(today, "yyyy-MM-dd"));
        setEndDate(format(today, "yyyy-MM-dd"));
        break;
      case "7d":
        setStartDate(format(subDays(today, 7), "yyyy-MM-dd"));
        setEndDate(format(today, "yyyy-MM-dd"));
        break;
      case "30d":
        setStartDate(format(subDays(today, 30), "yyyy-MM-dd"));
        setEndDate(format(today, "yyyy-MM-dd"));
        break;
      case "month":
        setStartDate(format(startOfMonth(today), "yyyy-MM-dd"));
        setEndDate(format(endOfMonth(today), "yyyy-MM-dd"));
        break;
      case "all":
        setStartDate("2020-01-01");  // All time - far back enough
        setEndDate(format(today, "yyyy-MM-dd"));
        break;
      default:
        break;
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedCaller && selectedCaller !== "all") params.append("caller_id", selectedCaller);

      const [dispRes, perfRes, dailyRes, trendRes, teamRes, sourceRes] = await Promise.all([
        axios.get(`${API}/crm-analytics/disposition-summary?${params}`, { withCredentials: true }),
        axios.get(`${API}/crm-analytics/caller-performance?${params}`, { withCredentials: true }),
        axios.get(`${API}/crm-analytics/daily-report?date=${endDate}`, { withCredentials: true }),
        axios.get(`${API}/crm-analytics/trends?${params}`, { withCredentials: true }).catch(() => ({ data: { trends: [] } })),
        axios.get(`${API}/team/users`, { withCredentials: true }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/crm-analytics/source-summary?${params}`, { withCredentials: true }).catch(() => ({ data: { summary: [] } }))
      ]);

      setDispositionSummary(dispRes.data.summary || []);
      setCallerPerformance(perfRes.data.performance || []);
      setDailyReport(dailyRes.data);
      setTrendData(trendRes.data.trends || []);
      setTeamUsers(teamRes.data.users || []);
      setSourceSummary(sourceRes.data.summary || []);
    } catch (error) {
      console.error("Failed to fetch analytics data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAIInsights = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/crm-analytics/ai-insights`, {
        start_date: startDate,
        end_date: endDate
      }, { withCredentials: true });
      setAiInsights(response.data);
      toast.success("AI insights generated!");
    } catch (error) {
      toast.error("Failed to generate AI insights");
    } finally {
      setAiLoading(false);
    }
  };

  const exportReport = async (type) => {
    try {
      const response = await axios.get(`${API}/crm-analytics/export?type=${type}&start_date=${startDate}&end_date=${endDate}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `crm_report_${type}_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report exported!");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  // Calculate totals for disposition
  const totalCalls = dispositionSummary.reduce((acc, d) => acc + d.count, 0);
  const convertedCount = dispositionSummary.find(d => d.disposition === "converted")?.count || 0;
  const interestedCount = dispositionSummary.find(d => d.disposition === "interested")?.count || 0;
  const appointmentCount = dispositionSummary.find(d => d.disposition === "appointment_booked")?.count || 0;

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="crm-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">CRM Reports & Analytics</h1>
            <p className="text-slate-600 mt-1">Disposition-based reports, agent performance & AI insights</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={getAIInsights}
              disabled={aiLoading}
              className="border-purple-500 text-purple-600 hover:bg-purple-50"
              data-testid="ai-insights-btn"
            >
              {aiLoading ? (
                <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              AI Insights
            </Button>
            <Button 
              variant="outline" 
              onClick={() => fetchAllData()}
              data-testid="refresh-reports-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex gap-2">
                {["today", "7d", "30d", "month", "all"].map((range) => (
                  <Button
                    key={range}
                    variant={dateRange === range ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleDateRangeChange(range)}
                    className={dateRange === range ? "bg-cyan-700" : ""}
                  >
                    {range === "today" ? "Today" : range === "7d" ? "7 Days" : range === "30d" ? "30 Days" : range === "month" ? "This Month" : "All Time"}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setDateRange("custom"); }}
                  className="w-40"
                />
                <span className="text-slate-500">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setDateRange("custom"); }}
                  className="w-40"
                />
              </div>
              <Select value={selectedCaller} onValueChange={setSelectedCaller}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Agents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {teamUsers.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* AI Insights Panel */}
        {aiInsights && (
          <Card className="border-purple-200 bg-purple-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                <Sparkles className="w-5 h-5" />
                AI Insights & Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiInsights.insights?.map((insight, idx) => (
                  <div key={idx} className="p-4 bg-white rounded-lg border border-purple-200">
                    <div className="flex items-start gap-3">
                      {insight.type === "warning" ? (
                        <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      ) : insight.type === "success" ? (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <Brain className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{insight.title}</p>
                        <p className="text-sm text-slate-600 mt-1">{insight.description}</p>
                        {insight.action && (
                          <p className="text-sm text-purple-700 mt-2 font-medium">→ {insight.action}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {aiInsights.leads_needing_attention?.length > 0 && (
                <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="font-medium text-orange-800 mb-2">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {aiInsights.leads_needing_attention.length} Leads Need Immediate Attention
                  </p>
                  <div className="space-y-2">
                    {aiInsights.leads_needing_attention.slice(0, 5).map((lead, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span>{lead.name} - {lead.phone}</span>
                        <Badge variant="outline" className="text-orange-700">{lead.reason}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Calls Made</p>
                  <p className="text-2xl font-bold text-slate-900">{totalCalls}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <PhoneCall className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Interested Leads</p>
                  <p className="text-2xl font-bold text-green-600">{interestedCount}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Appointments Booked</p>
                  <p className="text-2xl font-bold text-purple-600">{appointmentCount}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Conversions</p>
                  <p className="text-2xl font-bold text-emerald-600">{convertedCount}</p>
                  <p className="text-xs text-slate-500">
                    {totalCalls > 0 ? ((convertedCount / totalCalls) * 100).toFixed(1) : 0}% rate
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="disposition" data-testid="tab-disposition">
              <Phone className="w-4 h-4 mr-2" />
              Dispositions
            </TabsTrigger>
            <TabsTrigger value="agents" data-testid="tab-agents">
              <Users className="w-4 h-4 mr-2" />
              Agent Performance
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="tab-sources">
              <Target className="w-4 h-4 mr-2" />
              By Source
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <TrendingUp className="w-4 h-4 mr-2" />
              Trends
            </TabsTrigger>
          </TabsList>

          {/* Disposition Tab */}
          <TabsContent value="disposition" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Disposition Pie Chart */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Disposition Distribution</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => exportReport("disposition")}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : dispositionSummary.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-slate-500">
                      No disposition data available
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dispositionSummary}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="label"
                        >
                          {dispositionSummary.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={dispositionColors[entry.disposition] || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [value, name]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Disposition Table with Drill-down */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Disposition Breakdown</CardTitle>
                  <p className="text-sm text-slate-500">Click on a disposition to view leads</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[350px] overflow-y-auto">
                    {dispositionSummary.map((disp, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => window.location.href = `/leads?disposition=${disp.disposition}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: dispositionColors[disp.disposition] || COLORS[idx % COLORS.length] }}
                          />
                          <span className="font-medium">{disp.label}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold">{disp.count}</span>
                          <span className="text-sm text-slate-500">
                            {totalCalls > 0 ? ((disp.count / totalCalls) * 100).toFixed(1) : 0}%
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-cyan-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Disposition Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Disposition Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dispositionSummary} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" fontSize={12} />
                    <YAxis 
                      dataKey="label" 
                      type="category" 
                      stroke="#64748b" 
                      fontSize={11}
                      width={120}
                    />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {dispositionSummary.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={dispositionColors[entry.disposition] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agent Performance Tab */}
          <TabsContent value="agents" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Agent Performance Summary</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => exportReport("agents")}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-32 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : callerPerformance.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No agent performance data available</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Agent</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-700">Total Calls</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-700">Interested</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-700">Appointments</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-700">Conversions</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-700">Conv. Rate</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-700">Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {callerPerformance.map((agent, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                                  <span className="text-cyan-700 font-medium">
                                    {agent.caller_name?.charAt(0) || "?"}
                                  </span>
                                </div>
                                <span className="font-medium">{agent.caller_name || agent._id}</span>
                              </div>
                            </td>
                            <td className="text-center py-3 px-4 font-semibold">{agent.total_calls}</td>
                            <td className="text-center py-3 px-4">
                              <Badge className="bg-green-100 text-green-800">{agent.interested}</Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge className="bg-purple-100 text-purple-800">{agent.appointments}</Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge className="bg-emerald-100 text-emerald-800">{agent.conversions}</Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <span className={`font-bold ${
                                agent.conversion_rate >= 15 ? 'text-green-600' :
                                agent.conversion_rate >= 10 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {agent.conversion_rate}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${
                                    agent.conversion_rate >= 15 ? 'bg-green-500' :
                                    agent.conversion_rate >= 10 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(agent.conversion_rate * 5, 100)}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Agent Comparison Chart */}
            {callerPerformance.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Agent Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={callerPerformance}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="caller_name" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                      <Legend />
                      <Bar dataKey="total_calls" name="Total Calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="interested" name="Interested" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="conversions" name="Conversions" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* By Source Tab */}
          <TabsContent value="sources" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Source Pie Chart */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Lead Sources Distribution</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => exportReport("sources")}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : sourceSummary.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-slate-500">
                      No source data available
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={sourceSummary}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="label"
                        >
                          {sourceSummary.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [value, name]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Source Performance Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Source Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[350px] overflow-y-auto">
                    {sourceSummary.map((src, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <span className="font-medium">{src.label}</span>
                          </div>
                          <span className="text-lg font-bold">{src.count}</span>
                        </div>
                        <div className="flex gap-4 text-xs">
                          <span className="text-green-600">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            Interested: {src.interested || 0}
                          </span>
                          <span className="text-purple-600">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Appts: {src.appointment_booked || 0}
                          </span>
                          <span className="text-emerald-600">
                            <UserCheck className="w-3 h-3 inline mr-1" />
                            Conv: {src.converted || 0} ({src.conversion_rate || 0}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Source Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Lead Sources Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sourceSummary}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={10} angle={-45} textAnchor="end" height={80} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Legend />
                    <Bar dataKey="count" name="Total Leads" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="interested" name="Interested" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="converted" name="Converted" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Source Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Lead Sources</p>
                  <p className="text-2xl font-bold text-slate-900">{sourceSummary.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Top Source</p>
                  <p className="text-xl font-bold text-blue-600">
                    {sourceSummary[0]?.label || "-"}
                  </p>
                  <p className="text-sm text-slate-500">{sourceSummary[0]?.count || 0} leads</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Best Conversion</p>
                  <p className="text-xl font-bold text-green-600">
                    {sourceSummary.sort((a, b) => (b.conversion_rate || 0) - (a.conversion_rate || 0))[0]?.label || "-"}
                  </p>
                  <p className="text-sm text-slate-500">
                    {sourceSummary[0]?.conversion_rate || 0}% rate
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total from All Sources</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {sourceSummary.reduce((acc, s) => acc + s.count, 0)}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Daily Activity Trends</CardTitle>
              </CardHeader>
              <CardContent>
                {trendData.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No trend data available</p>
                      <p className="text-sm mt-1">Trends will appear as more data is collected</p>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                      <Legend />
                      <Area type="monotone" dataKey="calls" name="Calls" stroke="#3b82f6" fill="#3b82f680" />
                      <Area type="monotone" dataKey="interested" name="Interested" stroke="#22c55e" fill="#22c55e80" />
                      <Area type="monotone" dataKey="conversions" name="Conversions" stroke="#10b981" fill="#10b98180" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Today's Report Summary */}
            {dailyReport && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Today's Activity Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-blue-600">{dailyReport.summary?.calls_made || 0}</p>
                      <p className="text-sm text-slate-600">Calls Made</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-green-600">{dailyReport.summary?.whatsapp_sent || 0}</p>
                      <p className="text-sm text-slate-600">WhatsApp Sent</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-purple-600">{dailyReport.summary?.followups_scheduled || 0}</p>
                      <p className="text-sm text-slate-600">Follow-ups Set</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-emerald-600">{dailyReport.summary?.conversions || 0}</p>
                      <p className="text-sm text-slate-600">Conversions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
